<template>
  <article class="navigation-tabs__wrapper">
    <div class="navigation-tabs__tabs">
      <v-btn
        :to="{ name: RouteName.BillingMyTariff }"
        :class="['navigation-tabs__tab', { 'navigation-tabs__tab_active': isMyTariffPage }]"
      >
        <v-icon left>
          mdi-view-grid
        </v-icon>
        Мой тариф
      </v-btn>
      <v-btn
        :to="{ name: RouteName.BillingWriteOffs }"
        :class="['navigation-tabs__tab', { 'navigation-tabs__tab_active': isWriteOffsPage }]"
      >
        <v-icon left>
          mdi-file-search-outline
        </v-icon>
        Списания
      </v-btn>
      <v-btn
        :to="{ name: RouteName.BillingPaymentsHistory }"
        :class="['navigation-tabs__tab', { 'navigation-tabs__tab_active': isPaymentHistoryPage }]"
      >
        <v-icon left>
          mdi-history
        </v-icon>
        История платежей
      </v-btn>
      <v-btn
        :to="{ name: RouteName.BillingPaymentMethods }"
        :class="['navigation-tabs__tab', { 'navigation-tabs__tab_active': isPaymentMethodsPage }]"
      >
        <v-icon left>
          mdi-credit-card-outline
        </v-icon>
        Способы оплаты
      </v-btn>
    </div>
    <div v-if="$scopedSlots.middle" class="navigation-tabs__middle">
      <slot name="middle"></slot>
    </div>
    <div class="navigation-tabs__right-part">
      <div class="navigation-tabs__balance mr-4">
        <div class="navigation-tabs__balance-title">
          ВАШ БАЛАНС
        </div>
        <div class="navigation-tabs__balance-value">
          {{ formattedBalance }}
        </div>
      </div>
      <v-btn
        v-if="canUseBilling"
        class="navigation-tabs__replenish-btn"
        color="primary"
        @click="goToTopup"
      >
        <v-icon>mdi-plus</v-icon>
      </v-btn>
    </div>
  </article>
</template>

<script>
  import { mapState } from 'vuex'
  import RouteName from '@/models/enums/RouteName'
  import permissions from '@/mixins/permissions'

  export default {
    name: 'NavigationTabs',
    mixins: [permissions],
    computed: {
      ...mapState('billing', ['paymentAccount']),
      RouteName () {
        return RouteName
      },
      isPaymentHistoryPage () {
        return this.$route.name === RouteName.BillingPaymentsHistory
      },
      isWriteOffsPage () {
        return this.$route.name === RouteName.BillingWriteOffs
      },
      isMyTariffPage () {
        return this.$route.name === RouteName.BillingMyTariff
      },
      isPaymentMethodsPage () {
        return this.$route.name === RouteName.BillingPaymentMethods
      },
      formattedBalance () {
        if (this.paymentAccount.balance === null) return '—'
        return this.$options.filters.amountWithCurrency0(this.paymentAccount.balance)
      },
    },
    created () {
      this.$store.dispatch('billing/fetchPaymentAccount').catch(() => {
        // Errors surface in BalanceCard's snackbar; this component is read-only.
      })
    },
    methods: {
      goToTopup () {
        this.$router.push({ name: RouteName.BillingTopup })
      },
    },
  }
</script>

<style scoped lang="less">
.navigation-tabs {
  &__wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 15px;
    border: 2px solid #E1CD9D;
    border-radius: 10px;
    background-color: #FFF3D6;
    padding: 15px;

    .media-small-pc({
      flex-wrap: wrap;
    });

    .media-sm({
      flex-direction: column;
      align-items: stretch;
    });
  }

  &__tabs {
    display: flex;
    align-items: center;
    gap: 15px;

    // кнопки сеткой 2×2, средняя и правая колонки переносятся на вторую строку
    .media-small-pc({
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 220px));
      gap: 10px;
      flex: 1 1 100%;
    });

    .media-sm({
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 8px;
    });
  }

  &__middle {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    padding-left: 20px;
    border-left: 1px solid #E1CD9D;

    .media-small-pc({
      padding-left: 0;
      border-left: none;
    });

    .media-sm({
      width: 100%;
    });
  }

  &__tab {
    background-color: #F1F3F6 !important;
    border-radius: 5px;

    .media-small-pc({
      width: 100%;
    });

    // в узкой ячейке подпись переносится, кнопка растёт по высоте
    .media-sm({
      height: auto !important;
      min-height: 36px;
      padding: 6px 8px !important;
      font-size: 12px;
    });

    ::v-deep .v-btn__content {
      .media-sm({
        white-space: normal;
        line-height: 1.2;
      });
    }

    // гасим дефолтный hover/focus-overlay Vuetify, чтобы фон оставался чистым
    &::before {
      display: none;
    }

    .v-icon {
      color: @color-primary;
    }

    &:hover:not(.navigation-tabs__tab_active) {
      background-color: #D6D6D7 !important;
    }

    &_active {
      background-color: #000 !important;
      color: #fff;

      .v-icon {
        color: #fff;
      }
    }
  }

  &__right-part {
    display: flex;
    align-items: center;
    padding-left: 20px;
    border-left: 1px solid #E1CD9D;

    // в колонке вертикальная линия не к месту
    .media-sm({
      width: 100%;
      justify-content: space-between;
      padding-left: 0;
      border-left: none;
    });
  }

  &__balance {
    &-title {
      font-size: 12px;
      line-height: 1.2;
      color: #000;
    }

    &-value {
      font-size: 24px;
      font-weight: bold;
    }
  }

  &__replenish-btn {
    min-width: 48px !important;
    height: 48px !important;
    padding: 0 !important;
    border-radius: 5px;
  }
}
</style>
